import { useEffect, useState } from 'react'
import { Button } from '@arcanecircle/ui/grimoire/button'
import { useVTTStore } from '@/store'
import { socketService } from '@/services/socket'

/**
 * Floating offer card shown to the player nominated by the current GM
 * via `pass_gm_control`. Mounted once at the app root and conditionally
 * renders whenever `store.gmHandoffOffer` is set. Accept/Decline send
 * the matching socket events; the store slot is cleared by the
 * resulting `role_changed` / `gm_handoff_declined` / `gm_handoff_expired`
 * broadcast (handled in useWebSocket).
 *
 * The countdown updates every second from the server-supplied
 * `expiresAt` so users see real time remaining even if the tab was
 * backgrounded mid-offer. We do NOT auto-clear locally — the server's
 * TTL fires `gm_handoff_expired`, which is the authoritative dismissal
 * trigger.
 */
export function GMHandoffOffer() {
  const offer = useVTTStore((s) => s.gmHandoffOffer)
  const setOffer = useVTTStore((s) => s.setGmHandoffOffer)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!offer) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [offer])

  if (!offer) return null

  const secondsLeft = Math.max(0, Math.ceil((offer.expiresAt - now) / 1000))

  const handleAccept = () => {
    try {
      socketService.acceptGMControl(offer.fromUserId)
    } catch (err) {
      console.error('[GMHandoffOffer] acceptGMControl failed:', err)
    }
    // Optimistic dismiss — the role_changed broadcast will confirm.
    setOffer(null)
  }

  const handleDecline = () => {
    try {
      socketService.declineGMControl()
    } catch (err) {
      console.error('[GMHandoffOffer] declineGMControl failed:', err)
    }
    setOffer(null)
  }

  return (
    <div
      className="ac-card"
      role="alertdialog"
      aria-label="GM handoff offer"
      style={{
        position: 'fixed',
        top: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 250,
        maxWidth: 380,
        padding: '0.875rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
      }}
    >
      <div className="ac-panel-eyebrow">GM handoff offered</div>
      <div style={{ fontSize: '0.875rem' }}>
        <b>{offer.fromDisplayName}</b> wants to make you the GM of this session.
      </div>
      <div className="ac-help" style={{ fontSize: '0.75rem' }}>
        {secondsLeft > 0
          ? `Offer expires in ${secondsLeft}s.`
          : 'Offer expiring…'}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleDecline}
        >
          Decline
        </Button>
        <Button type="button" size="sm" onClick={handleAccept}>
          Accept
        </Button>
      </div>
    </div>
  )
}
